import { Location } from "@/types/filters";
import { logger } from "./logger";

/**
 * Location Details Store - Middleware to preserve location metadata
 * that gets lost during URL serialization/deserialization
 */

const STORAGE_KEY = "location_details_cache";
const CACHE_EXPIRY_HOURS = 24; // Cache expires after 24 hours

interface LocationDetailsCache {
	[locationKey: string]: {
		location: Location;
		timestamp: number;
		expiresAt: number;
	};
}

interface StoredLocationDetails {
	cache: LocationDetailsCache;
	version: string;
}

/**
 * Generate a unique key for a location based on name and type
 */
function generateLocationKey(name: string, type: string): string {
	return `${name.toLowerCase().replace(/\s+/g, "_")}_${type}`;
}

/**
 * Check if cached data is expired
 */
function isExpired(expiresAt: number): boolean {
	return Date.now() > expiresAt;
}

/**
 * Get stored location details from localStorage
 */
function getStoredData(): LocationDetailsCache {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) return {};

		const data: StoredLocationDetails = JSON.parse(stored);

		// Clean up expired entries
		const cleanCache: LocationDetailsCache = {};
		Object.entries(data.cache || {}).forEach(([key, value]) => {
			if (!isExpired(value.expiresAt)) {
				cleanCache[key] = value;
			}
		});

		return cleanCache;
	} catch (error) {
		logger.warn("Failed to get stored location details:", error instanceof Error ? error.message : "Unknown error");
		return {};
	}
}

/**
 * Save location details cache to localStorage
 */
function saveStoredData(cache: LocationDetailsCache): void {
	try {
		const data: StoredLocationDetails = {
			cache,
			version: "1.0.0",
		};
		localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch (error) {
		logger.warn("Failed to save location details:", error instanceof Error ? error.message : error);
	}
}

/**
 * Store location details for later retrieval
 */
export function storeLocationDetails(location: Location): void {
	try {
		const cache = getStoredData();
		const key = generateLocationKey(location.name, location.type);
		const now = Date.now();

		cache[key] = {
			location: { ...location },
			timestamp: now,
			expiresAt: now + CACHE_EXPIRY_HOURS * 60 * 60 * 1000,
		};

		saveStoredData(cache);
		// logger.debug(`📍 Stored location details for: ${location.name} (${location.type})`);
	} catch (error) {
		logger.warn("Failed to store location details:", error);
	}
}

/**
 * Retrieve stored location details by name and type
 */
export function getLocationDetails(name: string, type: string): Location | null {
	try {
		const cache = getStoredData();
		const key = generateLocationKey(name, type);
		const cached = cache[key];

		if (!cached || isExpired(cached.expiresAt)) {
			return null;
		}

		// logger.debug(`📍 Retrieved location details for: ${name} (${type})`);
		return cached.location;
	} catch (error) {
		logger.warn("Failed to get location details:", error);
		return null;
	}
}

/**
 * Enrich locations array with stored details (coordinates, placeId, etc.)
 * This is used when parsing locations from URL
 */
export function enrichLocationsWithStoredDetails(locations: Location[]): Location[] {
	return locations.map(location => {
		// If location already has coordinates and id, return as-is
		if (location.coordinates && location.id) {
			return location;
		}

		// Try to get stored details
		const storedDetails = getLocationDetails(location.name, location.type);
		if (storedDetails) {
			return {
				...location,
				// Only override if current location doesn't have these details
				id: location.id || storedDetails.id,
				coordinates: location.coordinates || storedDetails.coordinates,
			};
		}

		return location;
	});
}

export interface ApolloLocation {
	id: string;
	name: string;
	coordinates: [number, number];
}
/**
 * Enrich locations array with Apollo locations data (for SSR scenarios)
 * This provides richer location data including gps, radius, and linkedLocalityNames
 */
export function enrichLocationsWithApolloData({
	locations,
	apolloLocations,
}: {
	locations: Location[];
	apolloLocations: ApolloLocation[];
}): Location[] {
	return locations.map(location => {
		// Try to find matching location in Apollo data
		const apolloLocation = apolloLocations.find(item => item.name?.toLowerCase() === location.name?.toLowerCase());

		if (apolloLocation) {
			return {
				...location,
				// Only override if current location doesn't have these details
				id: location.id || apolloLocation.id,
				coordinates:
					location.coordinates ||
					(apolloLocation.coordinates && apolloLocation.coordinates.length >= 2
						? {
								latitude: apolloLocation.coordinates[1],
								longitude: apolloLocation.coordinates[0],
							}
						: undefined),
			};
		}

		return location;
	});
}

/**
 * Store multiple locations at once
 */
export function storeMultipleLocationDetails(locations: Location[]): void {
	locations.forEach(location => {
		if (location.coordinates || location.id) {
			storeLocationDetails(location);
		}
	});
}

/**
 * Clear expired entries from cache (can be called periodically)
 */
export function cleanupLocationCache(): void {
	try {
		const cache = getStoredData();
		const cleanCache: LocationDetailsCache = {};
		let removedCount = 0;

		Object.entries(cache).forEach(([key, value]) => {
			if (!isExpired(value.expiresAt)) {
				cleanCache[key] = value;
			} else {
				removedCount++;
			}
		});

		if (removedCount > 0) {
			saveStoredData(cleanCache);
			logger.debug(`📍 Cleaned up ${removedCount} expired location entries`);
		}
	} catch (error) {
		logger.warn("Failed to cleanup location cache:", error);
	}
}

/**
 * Clear all stored location details (for debugging or reset)
 */
export function clearLocationDetailsCache(): void {
	try {
		localStorage.removeItem(STORAGE_KEY);
		logger.debug("📍 Cleared all location details cache");
	} catch (error) {
		logger.warn("Failed to clear location details cache:", error);
	}
}
